import React, { SetStateAction, useEffect, useMemo, useState } from "react";
import { RiSearchLine } from "react-icons/ri";
import { useNavigate } from "react-router-dom";
import BaseButton from "Components/Base/BaseButton";
import TableContainer from "Components/Base/BaseTable";
import {
  checkStatusCodeSuccess,
  notFound,
  Search,
} from "Components/constants/Common";
import { NewLabel } from "Components/constants/RepairOrderOpening";
import { toast } from "react-toastify";
import DeleteModal from "Components/Base/DeleteModal";
import {
  deleteROReturn,
  listOfReturnRO,
  updateROReturnStatus,
} from "Api/ROReturn";
import { ROReturnLabels } from "Components/constants/ReturnRONPO";
import BaseModal from "Components/Base/BaseModal";
import {
  NoLabel,
  ROReturnPublishLabel,
  StatusLabel,
  YesLabel,
} from "Components/constants/Role";
import {
  ACTION_LABEL,
  DRAFT_LABEL,
  FULLY_RETURN_LABEL,
  PARTIALLY_RETURN_LABEL,
  PUBLISHED_LABEL,
  RO_NOT_FOUND_LABEL,
  RO_RETURN_LABEL,
  RO_RETURN_NUMBER_LABEL,
  RO_RETURN_STATUS_LABEL,
  RO_RETURN_STOCK_STATUS,
  SR_NO_LABEL,
} from "Components/constants/ROModule";

const ROReturnIndex = () => {
  const [loader, setLoader] = useState<boolean>(false);
  const [ROReturnData, setROReturnData] = useState<any[]>([]);
  const [ROReturnSearchData, setROReturnSearchData] = useState<Array<any>>([]);
  const [searchValue, setSearchValue] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [limit, setLimit] = useState<number>(10);
  const [totalPages, setTotalPages] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalNumberOfRows, setTotalNumberOfRows] = useState<number>(0);
  const navigate = useNavigate();
  const [btnLoader, setBtnLoader] = useState<boolean>(false);
  const [deleteModal, setDeleteModal] = useState<boolean>(false);
  const [sortOrder, setSortOrder] = useState<string>("desc");
  const [selectedROOId, setSelectedROOId] = useState<number | null>(null);
  const [columnName, setColumnName] = useState<string>("id");
  const [publishModalOpen, setPublishModalOpen] = useState<boolean>(false);

  const handleFetchData = (page: number) => {
    setCurrentPage(page);
  };

  const openDeleteModal = (id: number) => {
    setSelectedROOId(id);
    setDeleteModal(true);
  };
  const openPublishModal = (poid: number) => {
    setSelectedROOId(poid);
    setPublishModalOpen(true);
  };

  const closePublishModal = () => {
    setPublishModalOpen(false);
    setSelectedROOId(null);
  };

  const closeDeleteModal = () => {
    setDeleteModal(false);
    setSelectedROOId(null);
  };

  const handleFetchSorting = (
    page: number,
    id: SetStateAction<string>,
    order: SetStateAction<string>
  ) => {
    setCurrentPage(page);
    setColumnName(id);
    setSortOrder(order);
  };

  const fetchROReturnList = async () => {
    const payload = {
      limit,
      page: currentPage,
      sortKey: columnName,
      sortValue: sortOrder,
    };

    setLoader(true);
    try {
      const res = await listOfReturnRO(payload);

      if (checkStatusCodeSuccess(res?.statusCode)) {
        setROReturnData(res?.data?.repairOrderReturn || []);
        setTotalRecords(res?.data?.totalItems || 0);
        setTotalPages(res?.data?.totalPages || 1);
        setCurrentPage(res?.data?.currentPage || 1);
        setTotalNumberOfRows(res?.data?.numberOfRows || 0);
      } else {
        setROReturnData([]);
        toast.error(res?.message);
      }
    } catch (err: any) {
      setROReturnData([]);
    } finally {
      setLoader(false);
    }
  };

  const fetchROReturnSearchList = async () => {
    const payload = {
      limit,
      page: currentPage,
      search: searchValue,
      sortKey: columnName,
      sortValue: sortOrder,
    };

    setLoader(true);
    try {
      const res = await listOfReturnRO(payload);

      if (checkStatusCodeSuccess(res?.statusCode)) {
        setROReturnSearchData(res?.data?.repairOrderReturn || []);
        setTotalRecords(res?.data?.totalItems || 0);
        setTotalPages(res?.data?.totalPages || 1);
        setCurrentPage(res?.data?.currentPage || 1);
        setTotalNumberOfRows(res?.data?.numberOfRows || 0);
      } else {
        setROReturnSearchData([]);
        toast.error(res?.message);
      }
    } catch (err: any) {
      setROReturnSearchData([]);
    } finally {
      setLoader(false);
    }
  };

  const handleUpdatePOStatus = () => {
    if (selectedROOId !== null) {
      setBtnLoader(true);

      updateROReturnStatus(selectedROOId)
        .then((res) => {
          if (checkStatusCodeSuccess(res?.statusCode)) {
            toast.success(res?.message);
            fetchROReturnList();
          } else {
            toast.error(res?.message);
          }
        })
        .catch((error) => {
          toast.error(error?.response?.data?.message || error?.message);
        })
        .finally(() => {
          setBtnLoader(false);
          closePublishModal();
        });
    } else {
      setBtnLoader(false);
    }
  };

  const handleDeleteROO = async () => {
    if (!selectedROOId) {
      toast.error(RO_NOT_FOUND_LABEL);
      return;
    }

    setBtnLoader(true);
    try {
      const res = await deleteROReturn(selectedROOId);
      if (checkStatusCodeSuccess(res?.statusCode)) {
        toast.success(res?.message);
        await fetchROReturnList();
      } else {
        toast.error(res?.message);
      }
    } catch (error: any) {
      toast.error(error?.response?.data?.message || error?.message);
    } finally {
      setBtnLoader(false);
      closeDeleteModal();
    }
  };

  useEffect(() => {
    if (searchValue) {
      fetchROReturnSearchList();
    } else {
      fetchROReturnList();
    }
  }, [currentPage, limit, searchValue, sortOrder, columnName]);

  const repairOrderColumns = useMemo(
    () => [
      {
        header: SR_NO_LABEL,
        accessorKey: "srNo",
        cell: (info: any) => info.row.index + 1,
        flex: 1.5,
        minWidth: 150,
        enableColumnFilter: false,
        enableSorting: false,
      },
      {
        header: RO_RETURN_NUMBER_LABEL,
        accessorKey: "ror_no",
        enableSorting: true,
        enableColumnFilter: false,
      },
      {
        header: RO_RETURN_STATUS_LABEL,
        accessorKey: "roReturnStatus",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const status = cell?.row?.original?.roReturnStatus;
          return (
            <div className="d-flex justify-content-start gap-2 icon">
              <span
                className={`badge ${
                  status === PUBLISHED_LABEL
                    ? "bg-success"
                    : status === DRAFT_LABEL
                    ? "bg-danger"
                    : "bg-warning"
                }`}
              >
                {status === PUBLISHED_LABEL
                  ? PUBLISHED_LABEL
                  : status === DRAFT_LABEL
                  ? DRAFT_LABEL
                  : ""}
              </span>
            </div>
          );
        },
      },
      {
        header: RO_RETURN_STOCK_STATUS,
        accessorKey: "roReturnStatusUpdate",
        enableSorting: true,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const stockStatus = cell?.row?.original?.roReturnStatusUpdate;
          return (
            <div className="d-flex justify-content-start gap-2 icon">
              <span
                className={`badge ${
                  stockStatus === FULLY_RETURN_LABEL
                    ? "bg-success"
                    : stockStatus === PARTIALLY_RETURN_LABEL
                    ? "bg-warning"
                    : ""
                }`}
              >
                {stockStatus === FULLY_RETURN_LABEL
                  ? FULLY_RETURN_LABEL
                  : stockStatus === PARTIALLY_RETURN_LABEL
                  ? PARTIALLY_RETURN_LABEL
                  : ""}
              </span>
            </div>
          );
        },
      },
      {
        header: ACTION_LABEL,
        accessorKey: "ActionKey",
        enableSorting: false,
        enableColumnFilter: false,
        cell: (cell: any) => {
          const status = cell?.row?.original?.roReturnStatus;
          const roReturnId = cell?.row?.original?.id;

          const isPublished = status === PUBLISHED_LABEL;

          return (
            <div className="d-flex justify-content-start enable-action gap-2 icon">
              <span className="tooltip-container">
                <i
                  className="mdi mdi-eye-outline fs-4 text-warning cursor-pointer"
                  onClick={() => {
                    navigate(`/viewROReturn/${roReturnId}`, {
                      state: { viewQuotation: true },
                    });
                  }}
                ></i>
                <span className="tooltip-text">{ROReturnLabels.View}</span>
              </span>
              <span className="tooltip-container">
                <i
                  className="mdi mdi-history fs-4 text-primary cursor-pointer"
                  onClick={() => {
                    navigate(`/ROReturnHistory/${roReturnId}`);
                  }}
                ></i>
                <span className="tooltip-text">{ROReturnLabels.History}</span>
              </span>
              <span
                className={`tooltip-container ${
                  isPublished ? "icon-disabled" : ""
                }`}
              >
                <i
                  className="mdi mdi-pencil-outline fs-4 text-info cursor-pointer"
                  onClick={() =>
                    !isPublished && navigate(`/editROReturn/${roReturnId}`)
                  }
                  style={{
                    opacity: isPublished ? 0.5 : 1,
                    cursor: isPublished ? "not-allowed" : "pointer",
                  }}
                ></i>
                <span className="tooltip-text">
                  {isPublished
                    ? "Cannot edit published items"
                    : ROReturnLabels.Edit}
                </span>
              </span>
              <span
                className={`tooltip-container ${
                  isPublished ? "icon-disabled" : ""
                }`}
              >
                <i
                  className="mdi mdi-delete-outline fs-4 text-danger cursor-pointer"
                  onClick={() => !isPublished && openDeleteModal(roReturnId)}
                  style={{
                    opacity: isPublished ? 0.5 : 1,
                    cursor: isPublished ? "not-allowed" : "pointer",
                  }}
                ></i>
                <span className="tooltip-text">
                  {isPublished
                    ? "Cannot delete published items"
                    : ROReturnLabels.Delete}
                </span>
              </span>
              <span
                className={`tooltip-container ${
                  isPublished ? "icon-disabled" : ""
                }`}
              >
                <i
                  className="mdi mdi-check fs-4 text-success cursor-pointer"
                  onClick={() => !isPublished && openPublishModal(roReturnId)}
                  style={{
                    opacity: isPublished ? 0.5 : 1,
                    cursor: isPublished ? "not-allowed" : "pointer",
                  }}
                ></i>
                <span className="tooltip-text">{ROReturnLabels.Publish}</span>
              </span>
            </div>
          );
        },
      },
    ],
    [navigate]
  );

  return (
    <div className="page-content">
      <div className="flex-grow-1 mx-3">
        <div className="mb-1 mx-2 d-flex justify-content-between align-items-center">
          <span className="fs-4 d-flex gap-4">{RO_RETURN_LABEL}</span>

          <div className="d-flex align-items-center gap-2">
            <BaseButton
              className="btn btn-success"
              onClick={() => navigate("/addROReturn")}
            >
              <i className="mdi mdi-plus" /> {NewLabel}
            </BaseButton>
          </div>
        </div>

        <div className="card py-2 px-4 overflow-x-auto">
          {loader ||
          ROReturnData.length > 0 ||
          ROReturnSearchData.length > 0 ? (
            <TableContainer
              customPageSize={limit}
              setCustomPageSize={setLimit}
              isGlobalFilter={true}
              columns={repairOrderColumns}
              data={searchValue ? ROReturnSearchData : ROReturnData || []}
              tableClass="table bg-white"
              manualPagination={true}
              manualFiltering={true}
              tableLoader={loader}
              hasManualPagination={true}
              SearchPlaceholder={Search}
              currentPage={currentPage}
              setCurrentPage={setCurrentPage}
              totalPages={totalPages}
              totalRecords={totalRecords}
              totalNumberOfRows={totalNumberOfRows}
              onSearch={setSearchValue}
              fetchData={handleFetchData}
              fetchSortingData={handleFetchSorting}
            />
          ) : (
            <div className="py-4 text-center">
              <div>
                <span className="fs-1 text-success">
                  <RiSearchLine />
                </span>
              </div>
              <div className="mt-4">
                <h5>{notFound.dataNotFound}</h5>
                <p className="text-muted">{notFound.thereIsNoDataFound}</p>
              </div>
            </div>
          )}
          <DeleteModal
            show={deleteModal}
            onCloseClick={closeDeleteModal}
            onDeleteClick={handleDeleteROO}
            btnLoader={btnLoader}
          />

          <BaseModal
            open={publishModalOpen}
            toggle={closePublishModal}
            submit={handleUpdatePOStatus}
            title={StatusLabel}
            submitLabel={YesLabel}
            submitLabelColor="success"
            loader={btnLoader}
            closeLabel={NoLabel}
            closeLabelColor="danger"
          >
            <>
              <div>{ROReturnPublishLabel}</div>
            </>
          </BaseModal>
        </div>
      </div>
    </div>
  );
};

export default ROReturnIndex;
